// Vista de Facturación — rol "administracion" (Marcela).
// Cierra el ciclo de ventas: los pedidos que despacha ventas caen acá como
// "Pendientes de facturar"; al facturarlos pasan a "Facturados".
//
// Fuente de datos: pedidos_despachados + pedidos_entregados (un pedido despachado
// puede seguir pendiente de entrega o ya entregado; la facturación es independiente).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";

function fechaCorta(ts) {
  const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (ts ? new Date(ts) : null));
  if (!d || isNaN(d)) return "";
  return d.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function FacturacionAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useApp();

  const [despachados, setDespachados] = useState([]);
  const [entregados, setEntregados] = useState([]);
  const [tab, setTab] = useState("pendientes"); // pendientes | facturados
  const [busy, setBusy] = useState(null); // id en proceso
  const deposito = "R8"; // Facturación es SOLO R8 (ISABELA va por otro lado).
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "pedidos_despachados"), snap =>
      setDespachados(snap.docs.map(d => ({ id: d.id, _col: "pedidos_despachados", ...d.data() }))));
    const u2 = onSnapshot(collection(db, "pedidos_entregados"), snap =>
      setEntregados(snap.docs.map(d => ({ id: d.id, _col: "pedidos_entregados", ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  // Merge sin duplicados (si estuviera en ambas, gana entregados) y filtrado por depósito.
  const todos = useMemo(() => {
    const map = new Map();
    for (const p of despachados) map.set(p.id, p);
    for (const p of entregados) map.set(p.id, p); // pisa al despachado si coincide
    return [...map.values()].filter(p => String(p.deposito || "").toUpperCase() === deposito);
  }, [despachados, entregados, deposito]);

  const fechaRef = (p) => p.entregadoAt || p.despachadoAt || p.updatedAt || null;

  const pendientes = useMemo(
    () => todos.filter(p => p.facturado !== true)
      .sort((a, b) => (fechaRef(b)?.seconds || 0) - (fechaRef(a)?.seconds || 0)),
    [todos]
  );
  const facturados = useMemo(
    () => todos.filter(p => p.facturado === true)
      .sort((a, b) => (b.facturadoAt?.seconds || 0) - (a.facturadoAt?.seconds || 0)),
    [todos]
  );

  async function marcarFacturado(p) {
    if (busy) return;
    setBusy(p.id);
    try {
      await updateDoc(doc(db, p._col, p.id), {
        facturado: true,
        facturadoAt: serverTimestamp(),
        facturadoPor: user?.uid || null,
      });
      toast?.success?.(`#${p.numero || p.id} facturado ✓`);
    } catch (e) {
      console.error("[facturacion] marcar", e);
      toast?.error?.("No se pudo marcar como facturado");
    } finally {
      setBusy(null);
    }
  }

  const lista = tab === "pendientes" ? pendientes : facturados;

  return (
    <div style={{ padding: "16px", maxWidth: isDesktop ? "1100px" : "720px", margin: "0 auto" }}>
      <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#0f766e", fontWeight: 600, fontSize: "0.85rem", padding: 0, marginBottom: "12px", cursor: "pointer" }}>
        ← Volver
      </button>

      <h2 style={{ margin: "0 0 4px", fontSize: "1.2rem", fontWeight: 800 }}>🧾 Facturación <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#0f766e", background: "#f0fdfa", border: "1.5px solid #99f6e4", padding: "2px 10px", borderRadius: "999px", verticalAlign: "middle" }}>🏭 R8</span></h2>
      <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "#64748b" }}>
        Pedidos de R8 despachados por ventas, listos para facturar. (ISABELA se factura por otro lado.)
      </p>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
        {[
          ["pendientes", `Pendientes de facturar (${pendientes.length})`, "#b45309", "#fffbeb", "#fde68a"],
          ["facturados", `Facturados (${facturados.length})`, "#15803d", "#f0fdf4", "#86efac"],
        ].map(([key, label, color, bg, border]) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                flex: 1, padding: "10px", borderRadius: "10px", cursor: "pointer",
                fontWeight: 700, fontSize: "0.82rem",
                border: `1.5px solid ${active ? border : "#e2e8f0"}`,
                background: active ? bg : "#fff",
                color: active ? color : "#64748b",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {lista.length === 0 ? (
        <p style={{ color: "#94a3b8", fontSize: "0.9rem", textAlign: "center", padding: "30px 0" }}>
          {tab === "pendientes" ? "No hay pedidos pendientes de facturar." : "Todavía no hay pedidos facturados."}
        </p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(auto-fill, minmax(320px, 1fr))" : "1fr", gap: "8px" }}>
          {lista.map(p => (
            <div key={p.id} style={{ border: "1.5px solid #e2e8f0", borderRadius: "12px", padding: "12px 14px", background: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                <span style={{ fontWeight: 800, fontSize: "0.92rem", color: "#0f172a" }}>#{p.numero || p.id}</span>
                {p.metodoEntrega && (
                  <span style={{ fontSize: "0.66rem", fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "2px 8px", borderRadius: "999px" }}>{p.metodoEntrega}</span>
                )}
                {p.deposito && (
                  <span style={{ fontSize: "0.66rem", fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "2px 8px", borderRadius: "999px" }}>{p.deposito}</span>
                )}
                {fechaCorta(fechaRef(p)) && (
                  <span style={{ marginLeft: "auto", fontSize: "0.72rem", color: "#94a3b8" }}>📅 {fechaCorta(fechaRef(p))}</span>
                )}
              </div>
              <p style={{ margin: "0 0 6px", fontSize: "0.9rem", fontWeight: 600, color: "#1e293b" }}>{p.cliente || "—"}</p>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.76rem", color: "#64748b" }}>
                  {(p.productos || []).length} ítem{(p.productos || []).length !== 1 ? "s" : ""}
                </span>
                {p.bultos > 0 && <span style={{ fontSize: "0.74rem", color: "#64748b" }}>📦 {p.bultos} bultos</span>}
                {p.paquetes > 0 && <span style={{ fontSize: "0.74rem", color: "#64748b" }}>📏 {p.paquetes} paquetes</span>}

                {tab === "pendientes" ? (
                  <button
                    onClick={() => marcarFacturado(p)}
                    disabled={busy === p.id}
                    style={{
                      marginLeft: "auto", padding: "8px 14px", borderRadius: "10px", border: "none",
                      background: busy === p.id ? "#cbd5e1" : "#0f766e", color: "#fff",
                      fontWeight: 700, fontSize: "0.8rem", cursor: busy === p.id ? "default" : "pointer",
                    }}
                  >
                    {busy === p.id ? "…" : "🧾 Marcar facturado"}
                  </button>
                ) : (
                  <span style={{ marginLeft: "auto", fontSize: "0.76rem", fontWeight: 700, color: "#15803d" }}>
                    ✓ Facturado {p.facturadoAt ? `· ${fechaCorta(p.facturadoAt)}` : ""}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
